import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
} from '@mellify/ui';
import {
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Save,
  RefreshCw,
  Users,
  Target,
  Heart,
  BarChart3,
  Mic,
  Lightbulb,
  Brain,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAIPersonaGeneration } from '../hooks/useAIPersonaGeneration';

interface AIGeneratePersonaModalProps {
  open: boolean;
  onClose: () => void;
}

const FUNNEL_STAGE_LABELS: Record<string, string> = {
  tofu: 'TOFU — Top of Funnel',
  mofu: 'MOFU — Middle of Funnel',
  bofu: 'BOFU — Bottom of Funnel',
};

const FUNNEL_STAGE_COLORS: Record<string, string> = {
  tofu: 'info',
  mofu: 'warning',
  bofu: 'success',
};

export const AIGeneratePersonaModal: React.FC<AIGeneratePersonaModalProps> = ({
  open,
  onClose,
}) => {
  const {
    generate,
    generatedPersona,
    isGenerating,
    generationError,
    savePersona,
    isSaving,
    reset,
  } = useAIPersonaGeneration();

  const [overview, setOverview] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [showAdditionalContext, setShowAdditionalContext] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    description: true,
    demographics: true,
    psychographics: true,
    content: false,
    tone: false,
    insights: true,
  });

  const handleClose = useCallback(() => {
    if (isGenerating || isSaving) return;
    setOverview('');
    setAdditionalContext('');
    setShowAdditionalContext(false);
    setExpandedSections({
      description: true,
      demographics: true,
      psychographics: true,
      content: false,
      tone: false,
      insights: true,
    });
    reset();
    onClose();
  }, [isGenerating, isSaving, reset, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setOverview('');
      setAdditionalContext('');
      setShowAdditionalContext(false);
      reset();
    }
  }, [open, reset]);

  const handleGenerate = useCallback(() => {
    generate({
      overview: overview.trim(),
      additionalContext: additionalContext.trim() || undefined,
    });
  }, [generate, overview, additionalContext]);

  const handleRegenerate = useCallback(() => {
    reset();
    handleGenerate();
  }, [reset, handleGenerate]);

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const canGenerate = overview.trim().length >= 10;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!isGenerating && !isSaving}
      closeOnEscape={!isGenerating && !isSaving}
      aria-label="AI Generate Persona"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-900/20">
            <Brain className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-100">
              AI Generate Persona
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Describe your target audience and AI will create a detailed persona using your knowledge base
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-5">
        {/* Generation Error */}
        {generationError && !generatedPersona && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/10 border border-danger-200 dark:border-danger-800">
            <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
            <p className="text-sm text-danger-700 dark:text-danger-300">
              {generationError}
            </p>
          </div>
        )}

        {/* Input Form — shown when no generated persona */}
        {!generatedPersona && !isGenerating && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
                Target Audience Overview <span className="text-danger-500">*</span>
              </label>
              <textarea
                value={overview}
                onChange={(e) => setOverview(e.target.value)}
                placeholder="Describe who you want to target. For example: 'Small business owners in the e-commerce space who struggle with social media marketing and are looking for affordable automation tools to grow their online presence...'"
                rows={5}
                className="block w-full px-3 py-2 text-sm rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors resize-none"
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-dark-400 dark:text-dark-500">
                  {overview.length < 10
                    ? `Minimum 10 characters (${10 - overview.length} more)`
                    : 'AI will analyze your knowledge base to create a detailed persona'}
                </p>
                <span className="text-xs text-dark-400">{overview.length}/3000</span>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdditionalContext(!showAdditionalContext)}
                className="flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {showAdditionalContext ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
                Additional context (optional)
              </button>
              {showAdditionalContext && (
                <textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Any extra details about your business, products, or specific requirements for this persona..."
                  rows={3}
                  className="mt-2 block w-full px-3 py-2 text-sm rounded-md border border-dark-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors resize-none"
                />
              )}
            </div>

            <div className="p-3 rounded-lg bg-primary-50/50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800/30">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary-500 shrink-0 mt-0.5" />
                <div className="text-xs text-dark-600 dark:text-dark-300 space-y-1">
                  <p className="font-medium">How it works:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-dark-500 dark:text-dark-400">
                    <li>AI analyzes your knowledge base to understand your products & services</li>
                    <li>Generates a complete persona with demographics, pain points, goals & more</li>
                    <li>Creates a detailed description optimized for AI campaign agents</li>
                    <li>Review the persona and save it to your workspace</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generation Progress */}
        {isGenerating && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                <Brain className="w-8 h-8 text-primary-500 animate-pulse" />
              </div>
              <div className="absolute -top-1 -right-1">
                <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-dark-900 dark:text-dark-100 mb-2">
              Generating Persona...
            </h3>
            <p className="text-sm text-dark-500 dark:text-dark-400 text-center max-w-md">
              AI is analyzing your knowledge base and creating a detailed persona profile. This may take 15–30 seconds.
            </p>
            <div className="flex items-center gap-6 mt-6 text-xs text-dark-400 dark:text-dark-500">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-success-500" />
                Analyzing knowledge base
              </span>
              <span className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Building persona
              </span>
            </div>
          </div>
        )}

        {/* Generated Persona Preview */}
        {generatedPersona && !isGenerating && (
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
            {/* Header with confidence */}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-dark-900 dark:text-dark-100">
                  {generatedPersona.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge
                    variant={FUNNEL_STAGE_COLORS[generatedPersona.funnelStage] as any || 'primary'}
                    size="sm"
                  >
                    {FUNNEL_STAGE_LABELS[generatedPersona.funnelStage] || generatedPersona.funnelStage}
                  </Badge>
                  <Badge variant="primary" size="sm">
                    {Math.round(generatedPersona.confidence * 100)}% confidence
                  </Badge>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <CollapsibleSection
              icon={<Users className="w-4 h-4" />}
              title="Description"
              expanded={expandedSections.description}
              onToggle={() => toggleSection('description')}
            >
              <p className="text-sm text-dark-600 dark:text-dark-300 whitespace-pre-line leading-relaxed">
                {generatedPersona.description}
              </p>
            </CollapsibleSection>

            {/* Demographics Section */}
            <CollapsibleSection
              icon={<Target className="w-4 h-4" />}
              title="Demographics"
              expanded={expandedSections.demographics}
              onToggle={() => toggleSection('demographics')}
            >
              <div className="grid grid-cols-2 gap-3">
                {generatedPersona.demographics.ageRangeMin != null && generatedPersona.demographics.ageRangeMax != null && (
                  <InfoItem
                    label="Age Range"
                    value={`${generatedPersona.demographics.ageRangeMin}–${generatedPersona.demographics.ageRangeMax}`}
                  />
                )}
                {generatedPersona.demographics.gender && (
                  <InfoItem label="Gender" value={generatedPersona.demographics.gender} />
                )}
                {generatedPersona.demographics.jobTitle && (
                  <InfoItem label="Job Title" value={generatedPersona.demographics.jobTitle} />
                )}
                {generatedPersona.demographics.industry && (
                  <InfoItem label="Industry" value={generatedPersona.demographics.industry} />
                )}
                {generatedPersona.demographics.location && (
                  <InfoItem label="Location" value={generatedPersona.demographics.location} />
                )}
              </div>
            </CollapsibleSection>

            {/* Psychographics Section */}
            <CollapsibleSection
              icon={<Heart className="w-4 h-4" />}
              title="Psychographics"
              expanded={expandedSections.psychographics}
              onToggle={() => toggleSection('psychographics')}
            >
              <div className="space-y-3">
                {generatedPersona.interests.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-dark-600 dark:text-dark-300 mb-1.5">
                      Interests
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {generatedPersona.interests.map((interest, i) => (
                        <Badge key={i} variant="dark" size="sm">
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {generatedPersona.painPoints.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-dark-600 dark:text-dark-300 mb-1.5">
                      Pain Points
                    </p>
                    <ul className="space-y-1.5">
                      {generatedPersona.painPoints.map((point, i) => (
                        <li
                          key={i}
                          className="text-xs text-dark-500 dark:text-dark-400 flex items-start gap-2"
                        >
                          <span className="text-danger-400 mt-0.5 shrink-0">•</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {generatedPersona.goals.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-dark-600 dark:text-dark-300 mb-1.5">
                      Goals
                    </p>
                    <ul className="space-y-1.5">
                      {generatedPersona.goals.map((goal, i) => (
                        <li
                          key={i}
                          className="text-xs text-dark-500 dark:text-dark-400 flex items-start gap-2"
                        >
                          <span className="text-success-400 mt-0.5 shrink-0">•</span>
                          {goal}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Content Preferences Section */}
            <CollapsibleSection
              icon={<BarChart3 className="w-4 h-4" />}
              title="Content Preferences"
              expanded={expandedSections.content}
              onToggle={() => toggleSection('content')}
            >
              <div className="space-y-3">
                {generatedPersona.contentPreferences.preferredContentTypes.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-dark-600 dark:text-dark-300 mb-1.5">
                      Content Types
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {generatedPersona.contentPreferences.preferredContentTypes.map((type, i) => (
                        <Badge key={i} variant="primary" size="sm">
                          {type.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {generatedPersona.preferredChannels.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-dark-600 dark:text-dark-300 mb-1.5">
                      Preferred Channels
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {generatedPersona.preferredChannels.map((ch, i) => (
                        <Badge key={i} variant="info" size="sm">
                          {ch}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Tone Preferences Section */}
            <CollapsibleSection
              icon={<Mic className="w-4 h-4" />}
              title="Tone Preferences"
              expanded={expandedSections.tone}
              onToggle={() => toggleSection('tone')}
            >
              <div className="grid grid-cols-2 gap-3">
                {generatedPersona.tonePreferences.customTone && (
                  <InfoItem label="Tone" value={generatedPersona.tonePreferences.customTone} />
                )}
                {generatedPersona.tonePreferences.customFormalityLevel && (
                  <InfoItem
                    label="Formality"
                    value={generatedPersona.tonePreferences.customFormalityLevel.replace('_', ' ')}
                  />
                )}
              </div>
              {generatedPersona.tonePreferences.notes && (
                <p className="text-xs text-dark-500 dark:text-dark-400 mt-2">
                  {generatedPersona.tonePreferences.notes}
                </p>
              )}
            </CollapsibleSection>

            {/* Knowledge Insights Section */}
            {generatedPersona.knowledgeInsights.length > 0 && (
              <CollapsibleSection
                icon={<Lightbulb className="w-4 h-4" />}
                title="Knowledge Base Insights"
                expanded={expandedSections.insights}
                onToggle={() => toggleSection('insights')}
              >
                <ul className="space-y-1.5">
                  {generatedPersona.knowledgeInsights.map((insight, i) => (
                    <li
                      key={i}
                      className="text-xs text-dark-500 dark:text-dark-400 flex items-start gap-2"
                    >
                      <Lightbulb className="w-3 h-3 text-warning-400 mt-0.5 shrink-0" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {/* Reasoning */}
            {generatedPersona.reasoning && (
              <div className="p-3 rounded-lg bg-dark-50 dark:bg-dark-800/50 border border-dark-200 dark:border-dark-700">
                <p className="text-xs font-medium text-dark-600 dark:text-dark-300 mb-1">
                  AI Reasoning
                </p>
                <p className="text-xs text-dark-500 dark:text-dark-400 leading-relaxed">
                  {generatedPersona.reasoning}
                </p>
              </div>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {!generatedPersona && !isGenerating && (
          <>
            <Button variant="outline" size="md" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Sparkles className="w-4 h-4" />}
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              Generate Persona
            </Button>
          </>
        )}

        {isGenerating && (
          <Button variant="outline" size="md" onClick={handleClose} disabled>
            Cancel
          </Button>
        )}

        {generatedPersona && !isGenerating && (
          <>
            <Button variant="outline" size="md" onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="outline"
              size="md"
              leftIcon={<RefreshCw className="w-4 h-4" />}
              onClick={handleRegenerate}
              disabled={isSaving}
            >
              Regenerate
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={
                isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )
              }
              onClick={savePersona}
              loading={isSaving}
              disabled={isSaving}
            >
              Save Persona
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

AIGeneratePersonaModal.displayName = 'AIGeneratePersonaModal';

// ─── Helper Components ──────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  icon: React.ReactNode;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  icon,
  title,
  expanded,
  onToggle,
  children,
}) => (
  <Card>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-dark-50 dark:hover:bg-dark-800/50 transition-colors"
    >
      <div className="flex items-center gap-2">
        <span className="text-primary-500">{icon}</span>
        <h4 className="text-xs font-semibold text-dark-900 dark:text-dark-100">{title}</h4>
      </div>
      {expanded ? (
        <ChevronUp className="w-3.5 h-3.5 text-dark-400" />
      ) : (
        <ChevronDown className="w-3.5 h-3.5 text-dark-400" />
      )}
    </button>
    {expanded && <CardBody className="pt-0 pb-3 px-3">{children}</CardBody>}
  </Card>
);

interface InfoItemProps {
  label: string;
  value: string;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <div>
    <p className="text-xs text-dark-400 dark:text-dark-500">{label}</p>
    <p className="text-xs font-medium text-dark-700 dark:text-dark-200 capitalize">{value}</p>
  </div>
);